import { useGlobalAction } from "@gadgetinc/react";
import { message } from "antd";
import { useContext, useEffect, useState } from "react";
import { api } from "../../../api";
import LoadingSpinner from "../../../externalComponents/LoadingSpinner";
import { Badge } from "../../../externalComponents/tailwind/badge";
import { Button } from "../../../externalComponents/tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../externalComponents/tailwind/dialog";
import { Text } from "../../../externalComponents/tailwind/text";
import { CustomerContext } from "../../../providers";

export default function AddWhatsappFlowModal({ open, onClose, onCreated }) {
  const { customer } = useContext(CustomerContext);
  const [creatingFlowId, setCreatingFlowId] = useState(null);

  const [
    { data: flows, fetching: isListFetching, error: listError },
    getPublishedFlows,
  ] = useGlobalAction(api.settings.getPublishedWhatsappFlows);

  useEffect(() => {
    if (open) {
      getPublishedFlows();
    }
  }, [open]);

  const handleClose = () => {
    setCreatingFlowId(null);
    onClose();
  };

  const handleSelectFlow = async (flow) => {
    setCreatingFlowId(flow.id);
    try {
      const newFlow = await api.whatsappFlow.create({
        name: flow.name,
        flowId: Number(flow.id),
        customer: { _link: customer.id },
      });
      onCreated?.(newFlow.id);
    } catch (error) {
      message.error(`Failed to add flow: ${error.message}`);
    } finally {
      setCreatingFlowId(null);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} size="lg">
      <DialogTitle>Add a WhatsApp flow</DialogTitle>
      <DialogBody>
        <Text className="mb-4 text-sm text-gray-500">
          Select a published WhatsApp flow to start tracking its responses.
        </Text>

        {isListFetching ? (
          <LoadingSpinner />
        ) : listError ? (
          <Text className="text-sm text-red-600">{listError.message}</Text>
        ) : flows && flows.length > 0 ? (
          <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
            {flows.map((flow) => (
              <button
                key={flow.id}
                type="button"
                disabled={!!creatingFlowId}
                onClick={() => handleSelectFlow(flow)}
                className="text-left border border-zinc-200 rounded-lg p-3 hover:bg-zinc-50 transition-colors disabled:opacity-50"
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold">{flow.name}</div>
                  {creatingFlowId === flow.id && <LoadingSpinner />}
                </div>
                {flow.categories?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {flow.categories.map((category) => (
                      <Badge key={category} color="zinc">
                        {category}
                      </Badge>
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <Text className="text-sm text-gray-500">
            No new published flows found. Publish a flow in WhatsApp Manager
            first, or all your published flows have already been added.
          </Text>
        )}
      </DialogBody>
      <DialogActions>
        <Button
          color="secondaryButton"
          onClick={handleClose}
          disabled={!!creatingFlowId}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
